// apps/curbpass/src/routes/vehicles/components/VehicleMobileList.tsx
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  VehicleType,
  VehicleTypeValue,
  VehicleCategory,
  VehicleCategoryValue,
} from "@app.automotus.io/components/common";
import { VehicleTableRow } from "../vehicles.types";

interface VehicleMobileListProps {
  rows: VehicleTableRow[];
  onRowClick: (row: VehicleTableRow) => void;
  onDeleteNewRow: (id: string) => void;
}

export const VehicleMobileList: React.FC<VehicleMobileListProps> = React.memo(
  ({ rows, onRowClick, onDeleteNewRow }) => {
    if (rows.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">No vehicles yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Tap Add Vehicle to get started.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ overflowY: "auto", maxHeight: "70vh", pb: "24px" }}>
        {rows.map((row) => {
          const typeLabel = row.type ? VehicleType.FromValue(row.type as VehicleTypeValue)?.label ?? row.type : null;
          const categoryLabel = row.category
            ? VehicleCategory.FromValue(row.category as VehicleCategoryValue)?.label ?? row.category
            : null;

          return (
            <Box
              key={row.id}
              onClick={() => {
                if (!row.isNew) {
                  onRowClick(row);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: row.isNew
                  ? Object.keys(row.errors ?? {}).length > 0
                    ? "#fde8e8"
                    : "#f0faf0"
                  : "background.paper",
                cursor: row.isNew ? "default" : "pointer",
                "&:active": !row.isNew ? { bgcolor: "#f5f7ff" } : undefined,
              }}
            >
              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Plate — primary identifier */}
                <Typography variant="body1" fontWeight={700} noWrap>
                  {row.licensePlate || "Unknown plate"}
                </Typography>

                {/* State · Make Model — secondary */}
                {(row.stateCode || row.make || row.model) && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {[row.stateCode, row.make && row.model ? `${row.make} ${row.model}` : row.make || row.model]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                  {typeLabel && <Chip label={typeLabel} size="small" sx={{ fontSize: 11, height: 20 }} />}
                  {categoryLabel && (
                    <Chip
                      label={categoryLabel}
                      size="small"
                      color={categoryLabel === "Commercial" ? "primary" : "default"}
                      variant="outlined"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  )}
                  {row.isNew && (
                    <Chip
                      label="Pending"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Action */}
              {row.isNew ? (
                <IconButton
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onDeleteNewRow(row.id);
                  }}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              ) : (
                <ChevronRightIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
              )}
            </Box>
          );
        })}
      </Box>
    );
  },
);

export default VehicleMobileList;
